/**
 * Auth Context — dual auth: local JWT (email/password) + Clerk
 *
 * Priority:
 * 1. If Clerk is active and user is signed in, Clerk's session cookie
 *    handles auth automatically (same-origin). We just fetch /api/auth/me
 *    to get the user profile.
 * 2. If a local JWT exists in localStorage, use that (Bearer token).
 * 3. Otherwise, unauthenticated.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser, useAuth as useClerkAuthHook } from '@clerk/clerk-react';
import type { User, ClientRole } from '../../shared/types/auth';
import { setUseClerkCookieAuth } from '../services/api/client';

const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

interface ClerkState {
  isSignedIn: boolean | undefined;
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
}

interface ClientMembership {
  clientId: string;
  role: ClientRole;
  status: string;
}

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  clientMemberships: ClientMembership[];
  getClientRole: (clientId: string) => ClientRole | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'auth_token';

/**
 * Core auth logic — receives Clerk state as a prop so hooks
 * are only called in the Clerk-aware wrapper.
 */
function AuthProviderInner({
  clerkState,
  children,
}: {
  clerkState: ClerkState | null;
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [clientMemberships, setClientMemberships] = useState<ClientMembership[]>([]);
  const loggingOutRef = useRef(false);

  const clerkIsSignedIn = clerkState?.isSignedIn;

  // ── Clerk loading timeout — safety net ────────────────────────────────
  // If Clerk SDK fails to load (wrong keys, network issues, etc.),
  // isSignedIn stays undefined forever. This timeout ensures the app
  // always becomes interactive.
  useEffect(() => {
    if (!clerkState || clerkIsSignedIn !== undefined) return;
    const timer = setTimeout(() => {
      console.warn('[Auth] Clerk loading timed out — proceeding without Clerk');
      setIsLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [clerkState, clerkIsSignedIn]);

  // ── Clerk session flow (cookie-based, same-origin) ────────────────────
  // Clerk's session cookie is sent automatically on same-origin requests.
  // We just need to call /api/auth/me to get the user profile — no Bearer
  // token management needed.
  useEffect(() => {
    if (!clerkState || loggingOutRef.current) return;
    if (clerkIsSignedIn === undefined) return; // Still loading

    if (!clerkIsSignedIn) {
      // Clerk loaded but user isn't signed in
      setUseClerkCookieAuth(false);
      // If no local JWT either, mark as unauthenticated
      if (!token) setIsLoading(false);
      return;
    }

    // Clerk is signed in — enable cookie auth mode (no Bearer tokens)
    setUseClerkCookieAuth(true);
    // Clear any stale Clerk Bearer tokens from localStorage
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);

    (async () => {
      try {
        // Cookie is sent automatically — no Authorization header needed
        const res = await fetch('/api/auth/me');

        if (res.ok && !loggingOutRef.current) {
          const data = await res.json();
          const { clientMemberships: memberships, ...userData } = data;
          setUser(userData);
          setClientMemberships(memberships || []);
        }
      } catch (err) {
        console.error('[Auth] Clerk session sync error:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [clerkIsSignedIn]);

  // ── Local JWT flow (password login only) ──────────────────────────────
  useEffect(() => {
    // Skip if Clerk is handling auth
    if (clerkState && clerkIsSignedIn) return;
    if (!token) {
      // Dev only: the server auto-authenticates requests as the first admin
      // when NODE_ENV !== 'production' (see server/middleware/auth.ts), so ask
      // it who we are before falling back to the login page.
      if (import.meta.env.DEV && !clerkState) {
        fetch('/api/auth/me')
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data && !loggingOutRef.current) {
              const { clientMemberships: memberships, ...userData } = data;
              setUser(userData);
              setClientMemberships(memberships || []);
            }
          })
          .catch(() => { /* fall through to login page */ })
          .finally(() => setIsLoading(false));
        return;
      }
      // Only resolve as unauthenticated once Clerk has definitively loaded.
      if (clerkIsSignedIn === false || !clerkState) {
        setIsLoading(false);
      }
      return;
    }

    fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Invalid token');
        return res.json();
      })
      .then((data) => {
        const { clientMemberships: memberships, ...userData } = data;
        setUser(userData);
        setClientMemberships(memberships || []);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        setClientMemberships([]);
      })
      .finally(() => setIsLoading(false));
  }, [token, clerkIsSignedIn]);

  // ── Local login ─────────────────────────────────────────────────────────
  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(err.error || 'Login failed');
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
  }, []);

  // ── Refresh user from server ────────────────────────────────────────────
  const refreshUser = useCallback(async () => {
    try {
      // If Clerk is active, cookie handles auth
      if (clerkState && clerkIsSignedIn) {
        const res = await fetch('/api/auth/me');
        if (res.ok) {
          const data = await res.json();
          const { clientMemberships: memberships, ...userData } = data;
          setUser(userData);
          setClientMemberships(memberships || []);
        }
        return;
      }
      // Local JWT
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (!currentToken) return;
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        const { clientMemberships: memberships, ...userData } = data;
        setUser(userData);
        setClientMemberships(memberships || []);
      }
    } catch { /* ignore */ }
  }, [clerkState, clerkIsSignedIn]);

  // ── Get client role helper ─────────────────────────────────────────────
  const getClientRole = useCallback((clientId: string): ClientRole | null => {
    const membership = clientMemberships.find((m) => m.clientId === clientId);
    return membership ? membership.role as ClientRole : null;
  }, [clientMemberships]);

  // ── Logout (both local + Clerk) ────────────────────────────────────────
  const logout = useCallback(async () => {
    loggingOutRef.current = true;
    setUseClerkCookieAuth(false);
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setClientMemberships([]);
    setIsLoading(false);

    if (clerkState?.isSignedIn) {
      try { await clerkState.signOut(); } catch { /* ignore */ }
    }
    loggingOutRef.current = false;
  }, [clerkState?.isSignedIn]);

  const value: AuthContextValue = {
    user,
    token,
    isAuthenticated: !!user,
    isAdmin: !!user && ['admin', 'operations'].includes(user.role),
    isLoading,
    clientMemberships,
    getClientRole,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Wrapper that calls Clerk hooks (only rendered inside ClerkProvider) */
function AuthProviderWithClerk({ children }: { children: React.ReactNode }) {
  const { isSignedIn } = useUser();
  const { getToken, signOut } = useClerkAuthHook();

  const clerkState: ClerkState = { isSignedIn, getToken, signOut };

  return <AuthProviderInner clerkState={clerkState}>{children}</AuthProviderInner>;
}

/** Wrapper without Clerk */
function AuthProviderWithoutClerk({ children }: { children: React.ReactNode }) {
  return <AuthProviderInner clerkState={null}>{children}</AuthProviderInner>;
}

export const AuthProvider = clerkEnabled ? AuthProviderWithClerk : AuthProviderWithoutClerk;

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
